import {
  Send as SendIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAgentChat } from '../hooks/useAgentChat.js';
import { useCanvasAgentFeedback } from '../hooks/useAgentFeedback.js';
import { useCanvasHighlight } from '../hooks/useCanvasHighlight.js';
import { useDiscoveryState } from '../hooks/useDiscoveryState.js';
import { useOrchestratorAgent } from '../hooks/useOrchestratorAgent.js';
import { selectActiveCanvasConnections, selectActiveCanvasId, selectActiveCanvasWidgets, useCanvasStore } from '../stores/canvasStore.js';
import type { DiscoveryLevel } from '../types/discovery.js';
import type { Widget } from '../types/index.js';
import { AgentChatErrorBoundary } from './AgentChatErrorBoundary.js';

interface EphemeralMessage {
  id: string;
  content: string;
  timestamp: number;
  type: 'user' | 'agent' | 'status';
}

interface AgentChatMinimalProps {
  onClose?: () => void;
}

// Helper function to clean message content from control characters and raw responses
const cleanMessageContent = (content: string): string => {
  if (!content) return '';
  
  // Check if content contains control characters or looks like raw response
  if (content.includes('<ctrl46>') || content.startsWith('response:')) {
    // Try to extract JSON and get meaningful message
    const jsonMatch = content.match(/\{.*?\}/s);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        return parsed.message || parsed.result || parsed.analysis || 'Action completed';
      } catch {
        // If JSON parsing fails, try to extract just the message part
        const messageMatch = content.match(/"message":\s*"([^"]+)"/);
        if (messageMatch) {
          return messageMatch[1];
        }
      }
    }
    
    // Fallback: remove control characters and clean up
    return content
      .replace(/<ctrl\d+>/g, '')
      .replace(/^response:\w+/, '')
      .replace(/\{content:/, '')
      .replace(/,name:[^}]+\}$/, '')
      .trim();
  }
  
  return content;
};

const AgentChatMinimalCore: React.FC<AgentChatMinimalProps> = () => {
  const [ephemeralMessages, setEphemeralMessages] = useState<EphemeralMessage[]>([]);
  const timersRef = useRef<Set<number>>(new Set());
  const lastProcessedMessageRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [bubbleTopBase, setBubbleTopBase] = useState<number>(96);
  // Hover close delay to allow moving cursor into popover without flicker
  const hoverCloseTimeoutRef = useRef<number | null>(null);
  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      window.clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    hoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsHovered(false);
      hoverCloseTimeoutRef.current = null;
    }, 120);
  }, [cancelHoverClose]);
  
  // Canvas state - use specific selectors to prevent re-renders
  const activeCanvasId = useCanvasStore(selectActiveCanvasId);
  const widgets = useCanvasStore(selectActiveCanvasWidgets);
  const connections = useCanvasStore(selectActiveCanvasConnections);
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => window.clearTimeout(timer));
      timersRef.current.clear();
      if (hoverCloseTimeoutRef.current) {
        window.clearTimeout(hoverCloseTimeoutRef.current);
        hoverCloseTimeoutRef.current = null;
      }
    };
  }, []);

  // Keep bubble baseline offset aligned with the input container height
  useEffect(() => {
    const updateBase = () => {
      const rect = inputContainerRef.current?.getBoundingClientRect();
      if (rect) {
        // 16px below the input container bottom
        setBubbleTopBase(rect.height + 32);
      }
    };
    updateBase();
    const onResize = () => updateBase();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-remove ephemeral messages after 10 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setEphemeralMessages(prev => {
        const filtered = prev.filter(msg => now - msg.timestamp < 10000);
        // Only update if messages were actually removed
        return filtered.length !== prev.length ? filtered : prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  
  // Memoize discovery state callbacks
  const onLevelChange = useCallback((_newLevel: DiscoveryLevel) => {
    addEphemeralMessage(`Discovery level up! Welcome to ${_newLevel}`, 'status');
  }, []);

  const onHintReceived = useCallback((hints: any) => {
    console.log('New discovery hints received:', hints);
  }, []);

  const discoveryOptions = useMemo(() => ({
    onLevelChange,
    onHintReceived
  }), [onLevelChange, onHintReceived]);

  // Discovery state management
  const {
    discoveryState,
    updateFromAgentResponse,
    trackWidgetCreation,
    trackConnection
  } = useDiscoveryState(discoveryOptions);
  
  // Canvas highlighting
  const { highlightWidget, highlightPath } = useCanvasHighlight();
  
  // Memoize agent feedback callbacks
  const onActionStarted = useCallback((action: any) => {
    addEphemeralMessage(`${action.description}...`, 'status');
  }, []);

  const onActionCompleted = useCallback((action: any) => {
    console.log('✅ Action completed:', action.description);
  }, []);

  const onActionFailed = useCallback((action: any) => {
    addEphemeralMessage(`Failed: ${action.description}`, 'status');
  }, []);

  // Memoize agent feedback options
  const agentFeedbackOptions = useMemo(() => ({
    enableRealTimeUpdates: true,
    maxRecentActions: 5,
    onActionStarted,
    onActionCompleted,
    onActionFailed
  }), [onActionStarted, onActionCompleted, onActionFailed]);

  // Agent feedback for real-time action tracking
  const {
    activeActions,
    hasActiveActions,
    executionStatus,
  } = useCanvasAgentFeedback(activeCanvasId || '', agentFeedbackOptions);
  
  // Real API hook (HTTP/streaming)
  const realAgent = useAgentChat({
    canvasId: activeCanvasId,
    widgets,
    connections,
  });
  
  // Memoize the canvas state to prevent re-renders
  const canvasStateForAgent = useMemo(() => ({
    canvasId: activeCanvasId,
    widgets: widgets.map((w: Widget) => ({
      id: w.id,
      type: w.type,
      title: w.config.title,
      position: w.position,
      outputs: w.outputs.reduce((acc: any, output: any) => {
        acc[output.id] = output.value;
        return acc;
      }, {}),
    })),
    connections: connections,
  }), [activeCanvasId, widgets, connections]);
  
  // Add ephemeral message
  const addEphemeralMessage = useCallback((content: string, type: 'user' | 'agent' | 'status' = 'agent') => {
    // Clean the content if it's from the agent
    const cleanedContent = type === 'agent' ? cleanMessageContent(content) : content;
    
    const message: EphemeralMessage = {
      id: `msg-${Date.now()}-${Math.random()}`,
      content: cleanedContent,
      timestamp: Date.now(),
      type,
    };
    
    setEphemeralMessages(prev => [message, ...prev]);
  }, []);

  // Memoize response handler to prevent re-renders
  const handleAgentResponse = useCallback((response: any) => {
    console.log('Agent response:', response);
    // Do not show assistant output in minimal chat; only update discovery state and highlights
    // Update discovery state from agent response
    updateFromAgentResponse(response);
    
    // Track widget creation from tool calls
    if (response.message?.toolCalls) {
      response.message.toolCalls.forEach((toolCall: any) => {
        if (toolCall.toolName === 'createWidget') {
          trackWidgetCreation(toolCall.args.type);
          // Highlight the newly created widget
          if (toolCall.result?.widgetId) {
            const timer = window.setTimeout(() => {
              highlightWidget(toolCall.result.widgetId, '#4CAF50', 2000, 'glow');
            }, 500);
            timersRef.current.add(timer);
          }
        } else if (toolCall.toolName === 'connectWidgets') {
          trackConnection('unknown', 'unknown', 'default');
          // Highlight the connection path
          if (toolCall.args.sourceWidgetId && toolCall.args.targetWidgetId) {
            const timer = window.setTimeout(() => {
              highlightPath(
                [toolCall.args.sourceWidgetId, toolCall.args.targetWidgetId],
                '#2196F3',
                3000,
                300
              );
            }, 500);
            timersRef.current.add(timer);
          }
        }
      });
    }
  }, [updateFromAgentResponse, trackWidgetCreation, highlightWidget, trackConnection, highlightPath, addEphemeralMessage]);

  const handleAgentError = useCallback((error: Error) => {
    console.error('Agent error:', error);
    addEphemeralMessage(`Error: ${error.message}`, 'status');
  }, [addEphemeralMessage]);

  // Orchestrator (in-app) agent hook
  const orchestratorAgentHook = useOrchestratorAgent();

  // Select agent implementation by env (default orchestrator)
  const agentMode = (import.meta as any)?.env?.VITE_AGENT_MODE || 'orchestrator';
  const activeAgent = agentMode === 'http' ? (realAgent as any) : (orchestratorAgentHook as any);
  const { messages, input, handleInputChange, handleSubmit, isLoading, error, sendMessage } = activeAgent as any;

  // Auto-submit helper for prefilled prompts
  const handlePrefilledSubmit = useCallback(async (prompt: string) => {
    try {
      if (typeof sendMessage === 'function') {
        await sendMessage(prompt);
      } else {
        handleInputChange({ target: { value: prompt } } as any);
        setTimeout(() => handleSubmit(), 0);
      }
    } catch (e) {
      console.error('Failed to submit prompt:', e);
    }
  }, [sendMessage, handleInputChange, handleSubmit]);
  
  // Watch for new assistant messages; do not echo assistant content
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== 'assistant') return;

    // Ensure we only process each assistant message once to avoid feedback loops.
    const messageKey =
      typeof lastMessage.id === 'string'
        ? lastMessage.id
        : `${messages.length}:${lastMessage.createdAt ?? ''}`;
    if (lastProcessedMessageRef.current === messageKey) return;
    lastProcessedMessageRef.current = messageKey;

    if (typeof lastMessage.content === 'object' && lastMessage.content !== null) {
      handleAgentResponse(lastMessage.content);
    }
  }, [messages, handleAgentResponse]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Add user message as ephemeral before submitting
      if (input.trim()) {
        addEphemeralMessage(input, 'user');
      }
      handleSubmit(e as any);
    }
  };

  const EXPANDED_WIDTH = 560; // px
  const COLLAPSED_SIZE = 48; // px circle
  const isOpen = isHovered || isFocused || Boolean(input.trim()) || hasActiveActions;
  // Popover should be controlled by direct user intent only (hover/focus), not by agent activity
  const popoverOpen = isHovered || isFocused;

  // Suggested prompts (profile-focused) anchored to input container
  const profileHeader = widgets.find((w) => w.type === 'profileHeader');
  const ticker = profileHeader?.outputs?.find((o: any) => o.id === 'ticker')?.value as string | undefined;
  const profileInput = profileHeader?.inputs?.find((i: any) => i.id === 'profile')?.value as any | undefined;
  const name = (profileInput && (profileInput.name as string | undefined)) || undefined;
  const subject = useMemo(() => {
    if (ticker && name) return `${name} (${ticker})`;
    if (ticker) return ticker;
    if (name) return name;
    return 'NVDA';
  }, [ticker, name]);
  const suggestedPrompts = useMemo(() => [
    `Give me the 24-hour wrap for ${subject}: price move, key news drivers, sentiment, and upcoming catalysts.`,
    `Create a 2nd Order Supplier Dashboard for ${subject}`,
  ], [subject]);

  return (
    <>
      {/* Floating Input Bar */}
      <motion.div
        ref={containerRef}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 20 }}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: isOpen ? EXPANDED_WIDTH : COLLAPSED_SIZE,
          height: COLLAPSED_SIZE,
          zIndex: 1300,
          pointerEvents: 'auto',
        }}
      >
        <Paper
          ref={inputContainerRef}
          elevation={12}
          sx={{
            background: alpha('#111', 0.45),
            backdropFilter: 'blur(14px) saturate(160%)',
            border: '1px solid',
            borderColor: alpha('#fff', 0.18),
            borderRadius: isOpen ? 50 : '50%',
            overflow: 'hidden',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            '&:hover': {
              borderColor: alpha('#fff', 0.25),
              boxShadow: `0 24px 48px ${alpha('#000', 0.35)}`,
            },
            // Cursor-follow glow
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(160px circle at var(--mx, -100px) var(--my, -100px), rgba(116,6,242,0.22), transparent 60%)',
              opacity: isOpen ? 1 : 0,
              transition: 'opacity 200ms ease',
              pointerEvents: 'none',
            },
          }}
          onMouseEnter={() => { cancelHoverClose(); setIsHovered(true); }}
          onMouseLeave={() => { scheduleHoverClose(); }}
          onMouseMove={(e: any) => {
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            (e.currentTarget as HTMLElement).style.setProperty('--mx', `${e.clientX - r.left}px`);
            (e.currentTarget as HTMLElement).style.setProperty('--my', `${e.clientY - r.top}px`);
          }}
        >
          {isOpen ? (
            <form onSubmit={(e) => {
              if (input.trim()) {
                addEphemeralMessage(input, 'user');
              }
              handleSubmit(e);
            }}>
              <TextField
                ref={inputRef}
                fullWidth
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(Boolean(input.trim()))}
                placeholder={hasActiveActions ? "Akem is working..." : "Ask Akem..."}
                disabled={isLoading}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 2 }}>
                      <motion.div
                        animate={hasActiveActions ? { rotate: 360 } : {}}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <SparkleIcon 
                          sx={{ 
                            color: hasActiveActions ? 'primary.main' : alpha('#fff', 0.5),
                            fontSize: 20,
                          }} 
                        />
                      </motion.div>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: 1 }}>
                      {isLoading ? (
                        <CircularProgress size={20} sx={{ color: alpha('#fff', 0.7), mr: 1 }} />
                      ) : (
                        <>
                          {input.trim() && (
                            <IconButton
                              type="submit"
                              size="small"
                              sx={{
                                color: 'primary.main',
                                '&:hover': { 
                                  background: alpha('#fff', 0.1),
                                }
                              }}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    px: 2,
                    py: 1.5,
                    fontSize: '1rem',
                    color: '#fff',
                    '& .MuiInputBase-input': {
                      '&::placeholder': {
                        color: alpha('#fff', 0.5),
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
            </form>
          ) : (
            <IconButton
              onClick={() => {
                setIsHovered(true);
                setTimeout(() => inputRef.current?.focus(), 50);
              }}
              size="small"
              sx={{
                width: COLLAPSED_SIZE,
                height: COLLAPSED_SIZE,
                color: 'primary.main',
              }}
            >
              <SparkleIcon />
            </IconButton>
          )}
        </Paper>

        {/* Execution Status Indicator */}
        <AnimatePresence>
          {hasActiveActions && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{
                position: 'absolute',
                bottom: -30,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <Chip
                size="small"
                label={`${activeActions.length} action${activeActions.length > 1 ? 's' : ''} ${executionStatus}`}
                sx={{
                  fontSize: '0.75rem',
                  height: 24,
                  background: alpha('#000', 0.8),
                  backdropFilter: 'blur(10px)',
                  color: '#fff',
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.2),
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      
      {/* Suggested prompts (visible when input is open) */}
      {popoverOpen && (
        <Box
          sx={{ position: 'absolute', right: 16, top: COLLAPSED_SIZE + 8, zIndex: 1400, pointerEvents: 'none' }}
        >
        <Paper
          onMouseEnter={() => { cancelHoverClose(); setIsHovered(true); }}
          onMouseLeave={() => { scheduleHoverClose(); }}
          sx={{ p: 1, maxWidth: 560, bgcolor: alpha('#111', 0.9), border: '1px solid', borderColor: alpha('#fff', 0.18), pointerEvents: 'auto' }}
        >
          <Box sx={{ px: 1, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Suggested prompts
            </Typography>
          </Box>
          <List dense sx={{ py: 0 }}>
            {suggestedPrompts.map((p, idx) => (
              <ListItemButton
                key={`min-profile-suggest-${idx}`}
                onClick={() => handlePrefilledSubmit(p)}
                sx={{ borderRadius: 1 }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'normal', color: '#fff' }}>
                  {p}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Paper>
        </Box>
      )}

      {/* Ephemeral Message Bubbles */}
      <AnimatePresence>
        {ephemeralMessages.map((msg, index) => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, y: -20, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { type: "spring", damping: 15 }
            }}
            exit={{ 
              opacity: 0, 
              y: 20, 
              scale: 0.8,
              transition: { duration: 0.3 }
            }}
            style={{
              position: 'absolute',
              top: bubbleTopBase + (index * 60),
              left: msg.type === 'user' ? 'auto' : '50%',
              right: msg.type === 'user' ? '10%' : 'auto',
              transform: msg.type === 'user' ? 'none' : 'translateX(-50%)',
              maxWidth: 400,
              zIndex: 1200 - index,
              pointerEvents: 'none',
            }}
          >
            <Paper
              elevation={6}
              sx={{
                px: 2,
                py: 1,
                background: msg.type === 'user' 
                  ? alpha('#2196F3', 0.9)
                  : msg.type === 'status'
                  ? alpha('#FF9800', 0.9)
                  : alpha('#000', 0.9),
                backdropFilter: 'blur(10px)',
                borderRadius: msg.type === 'user' ? '18px 18px 4px 18px' : '18px',
                opacity: 1 - ((Date.now() - msg.timestamp) / 10000) * 0.3,
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#fff',
                  fontSize: '0.875rem',
                }}
              >
                {msg.content}
              </Typography>
            </Paper>
          </motion.div>
        ))}
      </AnimatePresence>
    </>
  );
};

// Main export with error boundary
export const AgentChatMinimal: React.FC<AgentChatMinimalProps> = (props) => {
  return (
    <AgentChatErrorBoundary>
      <AgentChatMinimalCore {...props} />
    </AgentChatErrorBoundary>
  );
};
